import app from 'flarum/forum/app';
import Modal from 'flarum/common/components/Modal';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import Tooltip from 'flarum/common/components/Tooltip';
import Button from 'flarum/common/components/Button';
import Checkbox from 'flarum/common/components/Checkbox';
import Dropdown from 'flarum/common/components/Dropdown';
import Stream from 'flarum/common/utils/Stream';
import ItemList from 'flarum/common/utils/ItemList';
import classList from 'flarum/common/utils/classList';
import { debounce } from 'flarum/common/utils/throttleDebounce';
import icon from 'flarum/common/helpers/icon';

import GlobalApplicationProlongModal from '../components/GlobalApplicationProlongModal';

export default class GlobalApplicationControlModal extends Modal {
    oninit(vnode) {
        super.oninit(vnode);

        this.applications = {};
        this.isLoadingPage = true;

        app.store.all('applications').filter((application) => application.canEdit()).forEach((application) => {
            this.applications[application.id()] = {
                active: Stream(false),
                version: Stream(application.version()),
                prolong: Stream(null),
                status: Stream(application.status()),
                application: application
            };
        });

        this.isLoadingPage = false;
        m.redraw();
    }

    className() {
        return 'GlobalApplicationControlModal Modal--medium';
    }

    title() {
        return app.translator.trans('xtreh-phoenix.forum.cheatpanel.grid.applications.label');
    }

    content() {
        const applications = Object.values(this.applications).map((data) => data.application);

        const columns = this.columns().toArray();

        return (
            <div className="Modal-body">
                <div className="Form">
                    <div className="GlobalApplicationControlModal">
                        <section
                            className={classList(['GlobalApplicationControlModal-grid', this.isLoadingPage ? 'GlobalApplicationControlModal-grid--loadingPage' : 'GlobalApplicationControlModal-grid--loaded'])}
                            style={{ '--columns': columns.length }}
                            role="table"
                            // +1 to account for header
                            aria-rowcount={applications.length + 1}
                            aria-colcount={columns.length}
                            aria-live="polite"
                            aria-busy={this.isLoadingPage ? 'true' : 'false'}
                        >
                            {/* Render columns */}
                            {
                                columns.map((column, colIndex) => (
                                    <div className="GlobalApplicationControlModal-grid-header" role="columnheader" aria-colindex={colIndex + 1} aria-rowindex={1}>
                                        {column.name}
                                    </div>
                                ))
                            }
                            {/* Render application data */}
                            {
                                applications.map((application, rowIndex) =>
                                    columns.map((col, colIndex) => {
                                        const columnContent = col.content && col.content(application), columnTooltip = { tooltipVisible: col.tooltip?undefined:false, ...(col.tooltip && col.tooltip(application)) };
                                        return (
                                            <Tooltip { ...columnTooltip }>
                                                <div
                                                    className={classList(['GlobalApplicationControlModal-grid-rowItem', (rowIndex % 2 >= applications.length % 2) && 'GlobalApplicationControlModal-grid-rowItem--shaded'])}
                                                    data-application-id={application.id()}
                                                    data-column-name={col.itemName}
                                                    aria-colindex={colIndex + 1}
                                                    // +2 to account for 0-based index, and for the header row
                                                    aria-rowindex={rowIndex + 2}
                                                    role="cell"
                                                >
                                                    {columnContent}
                                                </div>
                                            </Tooltip>
                                        );
                                    })
                                )
                            }
                            {/* Loading spinner that shows when a new page is being loaded */}
                            {this.isLoadingPage && <LoadingIndicator size='medium' />}
                        </section>
                    </div>
                </div>
            </div>
        );
    }

    columns() {
        const columns = new ItemList();

        function filter_number_input(el) {
            if (el?.value != '') {
                if (parseInt(el.value) < parseInt(el.min)) el.value = el.min;
                if (parseInt(el.value) > parseInt(el.max)) el.value = el.max;
            }
        }

        columns.add(
            'name',
            {
                name: app.translator.trans('xtreh-phoenix.forum.cheatpanel.grid.global_control.grid.name'),
                content: (application) => application.name()
            },
            90
        );

        columns.add(
            'version',
            {
                name: app.translator.trans('xtreh-phoenix.forum.cheatpanel.grid.global_control.grid.version'),
                content: (application) => [
                    <input
                        title={app.translator.trans('xtreh-phoenix.forum.cheatpanel.grid.global_control.grid.version')}
                        id={'version-'+application.id()}
                        type={'number'}
                        min={0}
                        max={32768}
                        step={1}
                        value={this.applications[application.id()].version()}
                        required
                        oncreate={(vnode) => vnode?.dom.addEventListener('change', filter_number_input.bind(this, vnode?.dom)) }
                    />,
                    <Button
                        onclick={() => {
                            // update application attributes
                            this.applications[application.id()].version(Number(this.$(`#version-${application.id()}`).val()));
                            // send update request
                            app.request({
                                method: 'PATCH',
                                url: app.forum.attribute('apiUrl') + '/applications/global',
                                params: { application: application.id(), code: 5, value: this.applications[application.id()].version() },
                                errorHandler: this.onerror.bind(this)
                            }).then(() => {
                                let alert = app.alerts.show({ dismissible: true, type: 'success' }, app.translator.trans('xtreh-phoenix.forum.cheatpanel.grid.global_control.grid.success_version_changed'));
                                debounce(2500, () => app.alerts.dismiss(alert))();
                                m.redraw();
                            });
                        }}
                    >
                        {'set'}
                    </Button>
                ]
            },
            80
        );

        columns.add(
            'status',
            {
                name: app.translator.trans('xtreh-phoenix.forum.cheatpanel.grid.global_control.grid.status'),
                content: (application) => [
                    <select
                        id={'status-'+application.id()}
                        title={app.translator.trans('xtreh-phoenix.forum.cheatpanel.grid.global_control.grid.status')}
                        value={this.applications[application.id()].status().application}
                        disabled={application.status().application == 'frozen'}
                        onchange={(event) => {
                            // fix mobile ontouch select disabled option event trigger
                            if (event.target.options[event.target.selectedIndex].disabled) return event.preventDefault();
                            // update application attributes
                            this.applications[application.id()].status(Object.assign(this.applications[application.id()].status(), { application: event.target.value }));
                            // send update request
                            app.request({
                                method: 'PATCH',
                                url: app.forum.attribute('apiUrl') + '/applications/global',
                                params: { application: application.id(), code: 4, value: 'application:' + event.target.value },
                                errorHandler: this.onerror.bind(this)
                            });
                            /*.then(() => {
                                let alert = app.alerts.show({ dismissible: true, type: 'success' }, app.translator.trans('xtreh-phoenix.forum.cheatpanel.grid.global_control.grid.success_status_changed'));
                                debounce(2500, () => app.alerts.dismiss(alert))();
                                m.redraw();
                            });*/
                        }}
                        required
                    >
                        <option value="frozen" disabled>frozen</option>
                        <option value="undetected">undetected</option>
                        <option value="use at own risk">use at own risk</option>
                        <option value="detected">detected</option>
                    </select>
                ]
            },
            70
        );

        columns.add(
            'license',
            {
                name: app.translator.trans('xtreh-phoenix.forum.cheatpanel.grid.global_control.grid.license'),
                content: (application) => (
                    <Button
                        id={'license-'+application.id()}
                        className="Button Button--transparent GlobalApplicationControlList-editModalBtn"
                        title={app.translator.trans('xtreh-phoenix.forum.cheatpanel.grid.global_control.grid.reset')}
                        icon='fab fa-digital-ocean'
                        onclick={() => {
                            this.$(`#license-${application.id()}`).attr('disabled', true);
                            app.request({
                                method: 'PATCH',
                                url: app.forum.attribute('apiUrl') + '/applications/global',
                                params: { application: application.id(), code: 0, value: null },
                                errorHandler: this.onerror.bind(this)
                            }).then(() => {
                                this.$(`#license-${application.id()}`).removeAttr('disabled');
                                let alert = app.alerts.show({ dismissible: true, type: 'success' }, app.translator.trans('xtreh-phoenix.forum.cheatpanel.grid.global_control.grid.success_license_reset'));
                                debounce(2500, () => app.alerts.dismiss(alert))();
                                m.redraw();
                            });
                        }}
                    >
                        {app.translator.trans('xtreh-phoenix.forum.cheatpanel.grid.global_control.grid.reset')}
                    </Button>
                )
            },
            60
        );

        columns.add(
            'suspend',
            {
                name: app.translator.trans('xtreh-phoenix.forum.cheatpanel.grid.global_control.grid.suspend'),
                content: (application) => (
                    <Button
                        id={'suspend-'+application.id()}
                        className="Button Button--transparent GlobalApplicationControlList-editModalBtn"
                        title={application.status().application == 'frozen' ? app.translator.trans('xtreh-phoenix.forum.cheatpanel.grid.global_control.grid.unsuspend_button') : app.translator.trans('xtreh-phoenix.forum.cheatpanel.grid.global_control.grid.suspend_button')}
                        icon='fas fa-ban'
                        onclick={() => {
                            this.$(`#suspend-${application.id()}`).attr('disabled', true);
                            app.request({
                                method: 'PATCH',
                                url: app.forum.attribute('apiUrl') + '/applications/global',
                                params: { application: application.id(), code: 2 + (application.status().application == 'frozen'), value: null },
                                errorHandler: this.onerror.bind(this)
                            }).then(() => {
                                // update application attributes
                                this.applications[application.id()].status(Object.assign(this.applications[application.id()].status(), { previous: application.status().application, application: application.status().application == 'frozen' ? application.status().previous : 'frozen' }));
                                // allow to toggle button again
                                this.$(`#suspend-${application.id()}`).removeAttr('disabled');
                                m.redraw();
                            });
                        }}
                    >
                        {application.status().application == 'frozen' ? app.translator.trans('xtreh-phoenix.forum.cheatpanel.grid.global_control.grid.unsuspend_button') : app.translator.trans('xtreh-phoenix.forum.cheatpanel.grid.global_control.grid.suspend_button')}
                    </Button>
                )
            },
            50
        );

        columns.add(
            'prolong',
            {
                name: app.translator.trans('xtreh-phoenix.forum.cheatpanel.grid.global_control.grid.prolong'),
                content: (application) => (
                    <Button
                        id={'prolong-'+application.id()}
                        className="Button Button--transparent GlobalApplicationControlList-editModalBtn"
                        disabled={application.status().application == 'frozen'}
                        icon='far fa-clock'
                        onclick={() => app.modal.show(GlobalApplicationProlongModal, { application }, true)}
                    >
                        {app.translator.trans('xtreh-phoenix.forum.cheatpanel.grid.global_control.grid.prolong_button')}
                    </Button>
                )
            },
            40
        );

        return columns;
    }
}
